import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { map, Observable } from 'rxjs';
import {
  Address,
  AddressUpsert,
  FinancialContact,
  EmergencyContact,
  Company,
  Personal,
  UserProfileDesiredFunction,
  DesiredFunction,
  DesiredLocation,
  UserProfileDesiredLocation,
  Education,
  ProfileOptions,
  ProfilePersonalData,
  EducationRequest,
  WorkExperience,
  WorkExperienceRequest,
  ApplicationLanguageResponse,
  ApplicationLanguageRequest,
} from '@app/core/types/profile/profile.types';
import { ProfileLayout } from '@app/core/types/profile/profile-layout.types';

@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.url.api}/profile/me`;

  getPersonal(): Observable<Personal> {
    return this.http.get<Personal>(`${this.url}/personal`);
  }

  getEmergencyContact(): Observable<EmergencyContact> {
    return this.http.get<EmergencyContact>(`${this.url}/contact/emergency`);
  }

  getFinancialContact(): Observable<FinancialContact> {
    return this.http.get<FinancialContact>(`${this.url}/contact/financial`);
  }

  getCompany(): Observable<Company> {
    return this.http
      .get<Company>(`${this.url}/freelancer`);
  }

  getUserPicture(): Observable<any> {
    return this.http
      .get(`${this.url}/picture`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(map((response) => response));
  }

  updateCompany(freelancer: Company): Observable<Company> {
    return this.http
      .put<Company>(`${this.url}/freelancer`, { freelancer });
  }

  updateEmergencyContact(data: EmergencyContact): Observable<EmergencyContact> {
    return this.http.put<EmergencyContact>(`${this.url}/contact/emergency`, data);
  }

  updateFinancialContact(data: FinancialContact): Observable<FinancialContact> {
    return this.http.put<FinancialContact>(`${this.url}/contact/financial`, data);
  }

  setProfilePicture(file: File, eTag: string): Observable<string> {
    const data = new FormData();
    data.append('ProfilePicture', file);

    const headers = eTag ? new HttpHeaders().set('x-hohr-etag', eTag) : null;
    return this.http
      .put(`${this.url}/picture`, data, { headers: headers, responseType: 'arraybuffer', observe: 'response' })
      .pipe(map((x) => x?.headers?.get('x-hohr-etag')));
  }

  getAddress(): Observable<Address> {
    return this.http.get<Address>(`${this.url}/address`);
  }

  updateAddress(address: AddressUpsert): Observable<Address> {
    return this.http.put<Address>(`${this.url}/address`, address);
  }

  deleteAccount(reason: string): Observable<void> {
    return this.http.post<void>(`${this.url}/delete/request`, { reason });
  }

  getUserProfileDesiredFunctions(): Observable<DesiredFunction[]> {
    return this.http
      .get<{ desiredFunctions: DesiredFunction[] }>(`${this.url}/desiredfunction`)
      .pipe(map((response) => response.desiredFunctions));
  }

  createUserProfileDesiredFunction(desiredFunction: UserProfileDesiredFunction): Observable<DesiredFunction> {
    return this.http.post<DesiredFunction>(`${this.url}/desiredfunction`, desiredFunction);
  }

  deleteUserProfileDesiredFunction(functionId: string): Observable<void> {
    return this.http.delete<void>(`${this.url}/desiredfunction/${functionId}`);
  }

  getUserProfileDesiredLocations(): Observable<UserProfileDesiredLocation[]> {
    return this.http
      .get<{ desiredLocations: UserProfileDesiredLocation[] }>(`${this.url}/desiredlocation`)
      .pipe(map((response) => response.desiredLocations));
  }

  createUserProfileDesiredLocation(desiredLocation: UserProfileDesiredLocation): Observable<DesiredLocation> {
    return this.http.post<DesiredLocation>(`${this.url}/desiredlocation`, desiredLocation);
  }

  deleteUserProfileDesiredLocation(locationId: string): Observable<void> {
    return this.http.delete<void>(`${this.url}/desiredlocation/${locationId}`);
  }

  getProfileOptions(context: string = null): Observable<ProfileOptions> {
    return this.http.get<ProfileOptions>(`${this.url}/options`,{
      params: context ? new HttpParams().set('Context', context) : null,
    });
  }

  getLayout(): Observable<ProfileLayout> {
    return this.http.get<ProfileLayout>(`${this.url}/layout`);
  }

  getPersonalData(): Observable<ProfilePersonalData> {
    return this.http.get<ProfilePersonalData>(`${this.url}/personaldata`);
  }

  updatePersonalData(data: ProfilePersonalData): Observable<ProfilePersonalData> {
    return this.http.put<ProfilePersonalData>(`${this.url}/personaldata`, data);
  }

  confirmPersonalData(): Observable<void> {
    return this.http.post<void>(`${this.url}/personaldata/confirm`, {});
  }

  getUserProfileEducations(): Observable<Education[]> {
    return this.http
      .get<{ educations: Education[] }>(`${this.url}/education`)
      .pipe(map((response) => response.educations));
  }

  getEducation(educationId: string): Observable<Education> {
    return this.http.get<Education>(`${this.url}/education/${educationId}`);
  }

  deleteUserProfileEducation(educationId: string): Observable<void> {
    return this.http.delete<void>(`${this.url}/education/${educationId}`);
  }

  addUserProfileEducation(education:EducationRequest): Observable<Education>{
    return this.http.post<Education>(`${this.url}/education`, education);
  }

  getUserProfileWorkExperiences(): Observable<WorkExperience[]> {
    return this.http
      .get<{ workExperiences: WorkExperience[] }>(`${this.url}/workexperience`)
      .pipe(map((response) => response.workExperiences));
  }

  addUserProfileWorkExperience(workExperience: Partial<WorkExperienceRequest>): Observable<WorkExperience> {
    return this.http.post<WorkExperience>(`${this.url}/workexperience`, workExperience);
  }

  getWorkExperience(workExperienceId: string): Observable<WorkExperience> {
    return this.http
      .get<WorkExperience>(`${this.url}/workexperience/${workExperienceId}`)
      .pipe(
        map((response: WorkExperience)=> ({
          ...response,
          from: this.mapIsoStringToMonthAndYear(response.from),
          to: response.to ? this.mapIsoStringToMonthAndYear(response.to) : null
        }))
      );
  }

  deleteUserProfileWorkExperience(workExperienceId: string): Observable<null> {
    return this.http.delete<null>(`${this.url}/workexperience/${workExperienceId}`);
  }

  getApplicationLanguage(): Observable<ApplicationLanguageResponse> {
    return this.http.get<ApplicationLanguageResponse>(`${this.url}/applicationLanguage`);
  }

  updateApplicationLanguage(request: ApplicationLanguageRequest): Observable<ApplicationLanguageResponse> {
    return this.http.put<ApplicationLanguageResponse>(`${this.url}/applicationLanguage`, request);
  }

  private mapIsoStringToMonthAndYear(isoString: string): string {
    return `${isoString.substring(5, 7)}/${isoString.substring(0, 4)}`;
  }
}
